<template>
  <v-container class="pa-0" fluid>
    <v-card>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row class="pt-3 pb-0">
            <v-col cols="8" class="py-0 pl-3">
              <span class="table-card-title pl-0">Carteiras de Cashback bloqueadas</span> <br />
              <p class="subtitle pt-1" style="word-break: break-word">
                Quando um consumidor com cashback ativado se identifica no PDV, sua carteira de cashback é bloqueada durante a transação para impedir que
                movimentações de saldo aconteçam durante a compra. No entanto, devido a eventuais falhas de comunicação entre o PDV e a Mercafacil, a carteira
                pode continuar bloqueada mesmo após a compra ter sido confirmada ou cancelada no PDV. Aqui você pode remover o bloqueio de carteiras de cashback
                de um consumidor da sua rede. Abaixo você confere os bloqueios que você tem.
              </p>
            </v-col>
            <v-col cols="4" class="py-0">
              <v-row class="pr-3">
                <v-col class="search-and-export-header" cols="12">
                  <div>
                    <mf-search
                      v-model="search"
                      placeholder="Busque por CPF/CNPJ"
                      hide-details
                      :disabled="!hasPiiAccess"
                      :tooltipMessage="!hasPiiAccess ? 'Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso' : ''"
                    />
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pt-0">
              <mf-info-card
                v-if="getSafe(() => converterLocks.records)"
                icon="error"
                :description="converterLocksWarning"
                variant="error"
                title="Existem saldos bloqueados na ativação de cashback"
              />
            </v-col>
          </v-row>
        </v-container>
        <paginated-data-table
          ref="table"
          :headers="headers"
          :search="search"
          :query="query"
          :custom-items-per-page="10"
          :queryName="'cashbackBalanceLocks'"
          :queryMainPropertyName="'cashback_balance_locks'"
          :queryVariables="queryVariables"
          default-table-sort-by="locked_at"
          :default-table-sort-desc="true"
          :graphql-uri="$microservicesUrls['cashback']"
          :loading="loading"
          @update:maxRecords="updateMaxRecords"
        >
          <template v-slot:item.user.document_no="{ item }">
            <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <template v-if="hasPiiAccess"> {{ getSafe(() => item.user.document_no) | cpfCnpj }}</template>
                  <template v-else>
                    {{ item.user.document_no }}
                  </template>
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </template>
          <template v-slot:item.user.name="{ item }">
            <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <template>
                    {{ item.user.name }}
                  </template>
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </template>
          <template v-slot:item.locked_by="{ item }">
            <mf-tooltip v-if="item.locked_by === 'converter'" max-width="250" top variant="secondary">
              <template #activator="{ on }"> <mf-icon fill small color="vermelho-0">warning</mf-icon> Ativação </template>
              <span>Carteira de cashback foi bloqueada durante uma ativação de cashback. Entre em contato com o suporte para desbloquear a carteira.</span>
            </mf-tooltip>
            <mf-tooltip v-else-if="item.locked_by === 'cashbackms'" max-width="250" top variant="secondary">
              <template #activator="{ on }"> Consumo forçado </template>
              <span
                >Carteira de cashback foi bloqueada durante um consumo forçado. Isso pode ocorrer durante um consumo fora do fluxo do PDV. A carteira pode ser
                liberada no menu a direita.</span
              >
            </mf-tooltip>
            <mf-tooltip v-else-if="item.locked_by === 'jane'" max-width="250" top variant="secondary">
              <template #activator="{ on }"> PDV </template>
              <span
                >Carteira de cashback foi bloqueada durante uma compra identificada no PDV. Isso ocorre quando é iniciada uma transação que não é confirmada ou
                cancelada no PDV. A carteira pode ser liberada no menu a direita.</span
              >
            </mf-tooltip>
            <span v-else>-</span>
          </template>
          <template v-slot:item.unit.name="{ item }">
            {{ getSafe(() => item.unit.name) | hifen }}
          </template>
          <template v-slot:item.transaction_id="{ item }">
            {{ item.locked_by === 'converter' ? '-' : item.transaction_id }}
          </template>
          <template v-slot:item.locked_value="{ item }"> {{ item.locked_value ? 'R$' : '' }}{{ item.locked_value | localeString(2) }} </template>
          <template v-slot:item.locked_at="{ item }">
            {{ DateTime.fromISO(item.locked_at).toFormat('dd/MM/yyyy HH:mm:ss') }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-menu>
              <template v-slot:activator="{ on }">
                <mf-button color="cinza-0" icon="more_vert" type="icon" v-on:click.native="on.click" />
              </template>
              <v-list>
                <v-list-item :disabled="item.locked_by === 'converter'" @click="unblockWallet({ documentNo: item.user.document_no })">
                  <v-list-item-title>Desbloquear carteira</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="item.locked_by === 'converter'" @click="unblockWalletAndConsumeBalance({ documentNo: item.user.document_no })">
                  <v-list-item-title>Desbloquear carteira e consumir saldo</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </paginated-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
// TODO: Retrigger load after adjustment.

import PaginatedDataTable from '@/components/PaginatedDataTable'
import {
  QUERY_GET_CASHBACK_BALANCE_LOCKS,
  QUERY_GET_CASHBACK_BALANCE_LOCKS_CONVERTER,
  MUTATION_RELEASE_CASHBACK_BALANCE_LOCK,
  MUTATION_RELEASE_CASHBACK_BALANCE_LOCK_AND_CONSUME_BALANCE,
} from '@/modules/mobile/cashback/graphql'
import { DateTime } from 'luxon'
import cpfCnpj from '@/filters/cpfCnpj.js'

export default {
  name: 'CashbackBalanceLocksTable',
  components: {
    PaginatedDataTable,
  },
  data: () => ({
    search: '',
    loading: false,
    maxRecords: 0,
  }),
  computed: {
    DateTime() {
      return DateTime
    },
    converterLocksWarning() {
      const converterLocksLabel =
        this.converterLocks.records === 1 ? `Existe ${this.converterLocks.records} bloqueio` : `Existem ${this.converterLocks.records} bloqueios`
      return `${converterLocksLabel} de carteira durante ativação de cashback. Este comportamento não é esperado, entre em contato com a equipe de suporte para resolver este problema.`
    },
    query() {
      return QUERY_GET_CASHBACK_BALANCE_LOCKS
    },
    queryVariables() {
      return {
        search: this.search,
      }
    },
    headers() {
      return [
        {
          text: 'CPF/CNPJ',
          value: 'user.document_no',
          width: '10%',
          sortable: false,
        },
        {
          text: 'Nome',
          value: 'user.name',
          width: '15%',
          sortable: false,
        },
        {
          text: 'Tipo de bloqueio',
          value: 'locked_by',
          width: '10%',
        },
        {
          text: 'Loja',
          value: 'unit.name',
          width: '15%',
          sortable: false,
        },
        {
          text: 'ID da transação',
          value: 'transaction_id',
          width: '15%',
        },
        {
          text: 'Data do bloqueio',
          value: 'locked_at',
          width: '10%',
        },
        {
          text: 'Saldo',
          value: 'locked_value',
          width: '5%',
        },
        {
          text: '',
          value: 'actions',
          width: '5%',
          sortable: false,
        },
      ]
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  apollo: {
    converterLocks: {
      query: QUERY_GET_CASHBACK_BALANCE_LOCKS_CONVERTER,
      context() {
        return {
          uri: this.$microservicesUrls['cashback'],
        }
      },
      update({ cashbackBalanceLocks }) {
        return cashbackBalanceLocks
      },
    },
  },
  methods: {
    getLockedByLabel(lockedBy) {
      if (!lockedBy) return '-'
      const lockedByLabels = {
        jane: 'PDV',
        converter: 'Ativação',
      }
      return lockedByLabels[lockedBy] || '-'
    },
    reload() {
      this.$refs.table.getData()
    },
    updateMaxRecords(records) {
      this.maxRecords = records
    },
    async unblockWallet({ documentNo }) {
      const confirmed = await this.$confirm({
        title: 'Desbloquear carteira sem consumir saldo',
        message: `Tem certeza que deseja desbloquear o consumo de cashback do cliente com o CPF ${cpfCnpj(
          documentNo
        )} sem consumir seu saldo? Ao confirmar essa ação, o saldo deste cliente será redisponibilizado.`,
        confirmText: 'Desbloquear',
        confirmColor: 'primary',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) {
        return
      }
      this.loading = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_RELEASE_CASHBACK_BALANCE_LOCK,
          variables: {
            document_no: documentNo,
          },
          context: {
            uri: this.$microservicesUrls['cashback'],
          },
        })
        this.reload()
      } catch (e) {
        this.$snackbar({ message: 'Erro ao ajustar cashback', snackbarColor: 'error' })
      }
      this.loading = false
    },
    async unblockWalletAndConsumeBalance({ documentNo }) {
      const confirmed = await this.$confirm({
        title: 'Desbloquear carteira consumindo o saldo',
        message: `Tem certeza que deseja desbloquear o consumo de cashback do CPF ${cpfCnpj(
          documentNo
        )} consumindo seu saldo? Ao confirmar essa ação, o saldo de cashback ativo será debitado da carteira deste cliente.`,
        confirmText: 'Desbloquear e consumir saldo',
        confirmColor: 'primary',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) {
        return
      }
      this.loading = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_RELEASE_CASHBACK_BALANCE_LOCK_AND_CONSUME_BALANCE,
          variables: {
            document_no: documentNo,
          },
          context: {
            uri: this.$microservicesUrls['cashback'],
          },
        })
        this.reload()
      } catch (e) {
        this.$snackbar({ message: 'Erro ao ajustar cashback', snackbarColor: 'error' })
      }
      this.loading = false
    },
  },
}
</script>
<style lang="scss" scoped>
.search-and-export-header {
  display: flex !important;
  justify-content: flex-end;
}

.search-and-export-header > span:first-child {
  align-self: center;
  margin: 0 8px 0 8px;
}

.search-and-export-header > div {
  width: 100%;
  max-width: 300px;
  min-width: 110px;
}
</style>
